import { useCallback } from 'react';
import { useMutation, graphql } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import RelayDataID from '@atlassian/relay-data-id';
import type {
	useSetMostRecentlyViewedBoardMutation,
	useSetMostRecentlyViewedBoardMutation$variables,
} from './__generated__/useSetMostRecentlyViewedBoardMutation.graphql';

export const META_ID = 'setMostRecentlyViewedBoard';
export const PACKAGE_NAME = 'horizontalNavSoftware';
export const TEAM_NAME = 'Navigation';

export const useSetMostRecentlyViewedBoard = () => {
	const [commitMutation] = useMutation<useSetMostRecentlyViewedBoardMutation>(graphql`
		mutation useSetMostRecentlyViewedBoardMutation($id: ID!) {
			jira @optIn(to: "JiraSetMostRecentlyViewedBoard") {
				setMostRecentlyViewedBoard(id: $id) {
					board {
						id
					}
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
				}
			}
		}
	`);

	const onError = useCallback((error: Error | MutationError) => {
		fireErrorAnalytics({
			meta: {
				id: META_ID,
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
	}, []);

	return useCallback(
		(id: useSetMostRecentlyViewedBoardMutation$variables['id'], projectRelayId?: string) => {
			commitMutation({
				variables: { id },
				onError,
				updater: (store, data) => {
					if (!data?.jira?.setMostRecentlyViewedBoard?.success || !projectRelayId) return;
					const parentRecord = store.get(projectRelayId);
					const newBoardDataId = RelayDataID(
						{ id: data?.jira?.setMostRecentlyViewedBoard?.board?.id },
						'JiraBoard',
					);
					const newBoardRecord = newBoardDataId && store.get(newBoardDataId);
					if (parentRecord && newBoardRecord) {
						parentRecord.setLinkedRecord(newBoardRecord, 'mostRecentlyViewedBoard');
					}
				},
				onCompleted: (response) => {
					const { errors } = response?.jira?.setMostRecentlyViewedBoard ?? {};
					if (errors) {
						const error = new MutationError(errors[0]);
						!error.statusCode?.toString().startsWith('4') && onError(error);
					}
				},
			});
		},
		[commitMutation, onError],
	);
};
